import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, FileUp, Loader2, Upload } from 'lucide-react';
import {
  importRejectedResponseSchema,
  type CsvDelimiter,
  type ImportError,
  type ImportPreviewResponse,
} from '@tekvoice/shared';
import { BiText } from '@/components/bi-text';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from '@/components/ui/sonner';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/utils';

const DELIMITER_LABELS: Record<CsvDelimiter, string> = {
  ',': 'comma (,)',
  ';': 'semicolon (;)',
  '\t': 'tab',
};

const ENCODING_LABELS: Record<string, string> = {
  'utf-8': 'UTF-8',
  'utf-8-bom': 'UTF-8 with BOM',
  'windows-1256': 'Windows-1256',
};

const number = new Intl.NumberFormat();

/** SPEC §5/§11 — dropzone → preview → name → commit. */
export function ImportWizardPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [preview, setPreview] = React.useState<ImportPreviewResponse | null>(null);
  const [errors, setErrors] = React.useState<ImportError[]>([]);
  const [name, setName] = React.useState('');
  const [dragging, setDragging] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const previewMutation = useMutation({
    mutationFn: (file: File) => api.imports.preview(file),
    onSuccess: (result) => {
      setErrors([]);
      setPreview(result);
      setName(result.suggestedName);
      toast.success(`${result.rowCount} row(s) read from ${result.sourceFilename}`);
    },
    onError: (error: Error) => {
      setPreview(null);
      if (error instanceof ApiError) {
        const rejected = importRejectedResponseSchema.safeParse(error.body);
        if (rejected.success) {
          setErrors(rejected.data.errors);
          toast.error(rejected.data.message);
          return;
        }
      }
      setErrors([]);
      toast.error(error.message);
    },
  });

  const commitMutation = useMutation({
    mutationFn: () => {
      if (!preview) throw new Error('No preview to commit');
      return api.imports.commit({ previewId: preview.previewId, name: name.trim() });
    },
    onSuccess: (result) => {
      toast.success(`Imported ${result.rowsCreated} row(s) into “${result.batch.name}”`);
      navigate(`/batches/${result.batch.id}`);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const handleFiles = (files: FileList | null): void => {
    const file = files?.[0];
    if (!file) return;
    previewMutation.mutate(file);
  };

  const creditEstimate = preview?.creditEstimate;
  const overBudget = creditEstimate?.sufficient === false;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
          <ArrowLeft className="h-4 w-4" />
          Batches
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Import CSV</h1>
          <p className="text-sm text-muted-foreground">
            Required columns: <code className="rounded bg-muted px-1">english_text</code> and{' '}
            <code className="rounded bg-muted px-1">arabic_text</code>. Every other column is passed
            through untouched.
          </p>
        </div>
      </div>

      {!preview ? (
        <Card>
          <CardContent className="pt-6">
            <div
              role="button"
              tabIndex={0}
              aria-label="Upload a CSV file"
              data-dragging={dragging}
              className={cn(
                'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-12 text-center transition-colors',
                dragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25',
                previewMutation.isPending && 'pointer-events-none opacity-60',
              )}
              onClick={() => inputRef.current?.click()}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') inputRef.current?.click();
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                handleFiles(event.dataTransfer.files);
              }}
            >
              {previewMutation.isPending ? (
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden />
              ) : (
                <FileUp className="h-8 w-8 text-muted-foreground" aria-hidden />
              )}
              <div>
                <p className="font-medium">
                  {previewMutation.isPending ? 'Reading the file…' : 'Drop a CSV here'}
                </p>
                <p className="text-sm text-muted-foreground">or click to choose a file</p>
              </div>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                aria-label="CSV file"
                onChange={(event) => handleFiles(event.target.files)}
              />
            </div>

            {errors.length > 0 ? (
              <Alert variant="destructive" className="mt-6">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>
                  This file was rejected — {errors.length} problem
                  {errors.length === 1 ? '' : 's'}
                </AlertTitle>
                <AlertDescription>
                  <p className="mb-2">
                    Nothing was imported. Fix the file and upload it again (SPEC §5: an import is
                    all-or-nothing).
                  </p>
                  <ul className="space-y-1">
                    {errors.map((error, index) => (
                      <li key={`${error.code}-${error.line ?? 'file'}-${index}`} className="text-sm">
                        <span className="font-medium">
                          {error.line === null ? 'File' : `Line ${error.line}`}
                          {error.column ? ` · ${error.column}` : ''}
                        </span>{' '}
                        — {error.message}{' '}
                        <code className="rounded bg-destructive/10 px-1 text-xs">{error.code}</code>
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{preview.sourceFilename}</CardTitle>
              <CardDescription>
                {number.format(preview.rowCount)} row(s) · showing the first{' '}
                {preview.sampleRows.length}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">
                  encoding: {ENCODING_LABELS[preview.detectedEncoding] ?? preview.detectedEncoding}
                </Badge>
                <Badge variant="secondary">
                  delimiter: {DELIMITER_LABELS[preview.detectedDelimiter]}
                </Badge>
                <Badge variant="secondary">english: {preview.englishHeader}</Badge>
                <Badge variant="secondary">arabic: {preview.arabicHeader}</Badge>
                {preview.passthroughHeaders.map((header) => (
                  <Badge key={header} variant="outline">
                    passthrough: {header}
                  </Badge>
                ))}
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>{preview.englishHeader}</TableHead>
                    <TableHead>{preview.arabicHeader}</TableHead>
                    {preview.passthroughHeaders.map((header) => (
                      <TableHead key={header}>{header}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.sampleRows.map((row) => (
                    <TableRow key={row.line}>
                      <TableCell className="text-muted-foreground tabular-nums">
                        {row.rowIndex + 1}
                      </TableCell>
                      <TableCell>
                        <BiText>{row.englishText}</BiText>
                      </TableCell>
                      <TableCell className="text-base">
                        {row.arabicText.length > 0 ? (
                          <BiText>{row.arabicText}</BiText>
                        ) : (
                          <span className="text-xs italic text-muted-foreground">blank</span>
                        )}
                      </TableCell>
                      {preview.passthroughHeaders.map((header) => (
                        <TableCell key={header} className="text-muted-foreground">
                          {row.extraValues[header] ?? ''}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {preview.warnings.length > 0 ? (
            <Alert variant="warning" data-testid="import-warnings">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>
                {preview.warnings.length} warning{preview.warnings.length === 1 ? '' : 's'} — import
                is still allowed
              </AlertTitle>
              <AlertDescription>
                <ul className="space-y-2">
                  {preview.warnings.map((warning) => (
                    <li key={warning.code}>
                      <span className="font-medium">{warning.message}</span>
                      <div className="text-xs">
                        line{warning.lines.length === 1 ? '' : 's'} {warning.lines.join(', ')}{' '}
                        <code className="rounded bg-amber-500/10 px-1">{warning.code}</code>
                      </div>
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="grid gap-4 md:grid-cols-2">
            <Card data-testid="character-totals">
              <CardHeader>
                <CardTitle className="text-base">Characters to speak</CardTitle>
                <CardDescription>Per language, after normalization.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>English</span>
                  <span className="tabular-nums">{number.format(preview.characterTotals.en)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Arabic</span>
                  <span className="tabular-nums">{number.format(preview.characterTotals.ar)}</span>
                </div>
                <div className="flex justify-between border-t pt-1 font-medium">
                  <span>Total</span>
                  <span className="tabular-nums">
                    {number.format(preview.characterTotals.total)}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="credit-estimate" className={cn(overBudget && 'border-destructive')}>
              <CardHeader>
                <CardTitle className="text-base">Credit estimate</CardTitle>
                <CardDescription>
                  {creditEstimate?.assetsQueued ?? 0} clip(s) would be generated.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Estimated credits</span>
                  <span className="tabular-nums">
                    {number.format(creditEstimate?.estimatedCredits ?? 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Remaining credits</span>
                  <span className="tabular-nums">
                    {creditEstimate?.remainingCredits === null ||
                    creditEstimate?.remainingCredits === undefined
                      ? 'unknown'
                      : number.format(creditEstimate.remainingCredits)}
                  </span>
                </div>
                {overBudget ? (
                  <p className="pt-2 font-medium text-destructive" role="status">
                    Over budget — this batch needs more credits than the account has left. You can
                    still import; generation will be blocked until credits are topped up.
                  </p>
                ) : creditEstimate?.sufficient === null ? (
                  <p className="pt-2 text-muted-foreground">
                    Could not read the live credit balance
                    {creditEstimate.lookupError ? `: ${creditEstimate.lookupError}` : ''}.
                  </p>
                ) : (
                  <p className="pt-2 text-muted-foreground">Within the remaining balance.</p>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Name this batch</CardTitle>
              <CardDescription>
                The name becomes the folder slug in Google Cloud Storage and cannot be changed later.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form
                className="flex flex-wrap items-end gap-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  commitMutation.mutate();
                }}
              >
                <div className="min-w-64 flex-1 space-y-2">
                  <Label htmlFor="batch-name">Batch name</Label>
                  <Input
                    id="batch-name"
                    required
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    disabled={commitMutation.isPending}
                  />
                </div>
                <Button type="submit" disabled={commitMutation.isPending || name.trim().length === 0}>
                  <Upload className="h-4 w-4" />
                  {commitMutation.isPending ? 'Importing…' : `Import ${preview.rowCount} row(s)`}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={commitMutation.isPending}
                  onClick={() => {
                    setPreview(null);
                    setErrors([]);
                  }}
                >
                  Choose a different file
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
